import { computed } from 'vue';

import { pad, propsFactory, splitDate } from '@components/utils';

import { useLocale } from '../composable';

import type { ILocale, TAvailableLocale } from '@components/locale';
import type { IRawDate, TDateLocale } from '@components/utils';
import type { ComputedRef, PropType } from 'vue';

export interface IDatetimeProps {
  modelValue: string | TDatetimeValueRange | Array<string | TDatetimeValueRange>;
  mask: string;
  locale?: ILocale | TAvailableLocale;
  readonly?: boolean;
  disabled?: boolean;
}
export interface IUseDatetime {
  editable: ComputedRef<boolean>;
  tabindex: ComputedRef<number>;
  getCurrentDate: (dateOnly?: boolean) => IRawDate;
  getShortDate: (date: IRawDate) => Partial<IRawDate>;
  getLocale: () => TDateLocale;
  getMask: () => string;
  decodeString: (date: string) => IRawDate;
}

export type TDatetimeValueRange = { from: string | IRawDate; to: string | IRawDate; target?: string | IRawDate };

export const makeDatetimeProps = propsFactory(
  {
    /**
     * Значение модели
     */
    modelValue: {
      type: [String, Array, Object] as PropType<string | TDatetimeValueRange | Array<string | TDatetimeValueRange>>,
      default: null
    },
    /**
     * Формат даты/времени
     */
    mask: {
      type: String as PropType<string>,
      default: 'DD.MM.YYYY'
    }
  },
  'datetime'
);

export function useDatetime(props: IDatetimeProps): IUseDatetime {
  const editable = computed(() => !props.disabled && !props.readonly);
  const tabindex = computed(() => (editable.value ? 0 : -1));
  const locale = useLocale(props);

  function getCurrentDate(dateOnly?: boolean): IRawDate {
    const date = new Date();
    const timeFill = dateOnly ? null : 0;

    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: timeFill as unknown as number,
      minute: timeFill as unknown as number,
      second: timeFill as unknown as number,
      millisecond: timeFill as unknown as number
    };
  }

  function getShortDate(date: IRawDate): Partial<IRawDate> {
    return { year: date.year, month: date.month, day: date.day };
  }

  function getLocale() {
    return locale.date;
  }

  function getMask() {
    return props.mask;
  }

  function decodeString(date: string): IRawDate {
    return splitDate(date, props.mask, getLocale(), {
      hour: 0,
      minute: 0,
      second: 0,
      millisecond: 0
    });
  }

  return {
    editable,
    tabindex,
    getCurrentDate,
    getShortDate,
    getLocale,
    getMask,
    decodeString
  };
}

export function getDayHash(date: IRawDate): string {
  return date.year + '/' + pad(date.month as number) + '/' + pad(date.day as number);
}
